/**
 * Завод, который менеджер искал в «Товарах», — для МСК Газобетона.
 *
 * Автовыбор ставит первым самый дешёвый вариант, и это часто другой завод:
 * менеджер искал Bonolit, а расчёт показывает Могилевский Газосиликат. Здесь
 * считается, что именно искали и какой завод искомого производителя выгоднее
 * всех, чтобы блок расчёта мог предложить вернуться к нему одной кнопкой.
 *
 * Своих цен не считается: на вход приходят те же строки, что показывает
 * «Сравнение стоимости производств», с их готовыми итогами. Производство здесь
 * не переключается — это только подсказка.
 */

// Слова, с которых начинается номенклатура, а не название завода.
const CATEGORY_WORDS = new Set(['газобетон', 'газоблок', 'u-блок', 'u-блоки', 'блок']);

const normalize = (value) => String(value || '')
  .toLowerCase()
  .replace(/ё/g, 'е')
  .replace(/[^a-zа-я0-9]/gi, '');

// Плотность («D400») и размер («300x250x600») — конец названия завода.
const isSpecToken = (word) => /^d\s*\d{3}/i.test(word) || /\d/.test(word);

/**
 * Название завода из номенклатуры: «Газобетон Bonolit D400 300×250×600 мм» →
 * «Bonolit», «Газобетон Могилевский Газосиликат D400 …» → «Могилевский
 * Газосиликат».
 */
export const extractGazBrand = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  const brand = [];

  for (const word of words) {
    if (brand.length === 0 && CATEGORY_WORDS.has(word.toLowerCase().replace(/[«»"]/g, ''))) {
      continue;
    }
    if (isSpecToken(word)) {
      break;
    }
    brand.push(word);
  }

  return brand.join(' ').replace(/[,;]+$/, '').trim() || null;
};

/**
 * Название завода из строки сравнения: «Bonolit (Старая Купавна)» → «Bonolit»,
 * «ВКблок, Сергиев Посад, Ксмк-М8» → «ВКблок». Город и источник прайса в имя
 * производителя не входят.
 */
export const extractProductionBrand = (production) => {
  const head = String(production || '').split(/[(,]/)[0];
  return head.trim() || null;
};

const totalOf = (row) => {
  const value = Number(row?.totalSum);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const productionOf = (row) => String(row?.production || row?.comparison?.production || '').trim();
const comparisonIdOf = (row) => String(row?.comparison?.id ?? row?.id ?? '').trim();

const isGazName = (name) => String(name || '').toLowerCase().includes('газобетон');

/** Что искал менеджер: завод из первой строки газобетона в «Товарах». */
const requestedBrandOf = (products) => {
  for (const product of (Array.isArray(products) ? products : [])) {
    if (!isGazName(product?.name)) {
      continue;
    }
    const brand = extractGazBrand(product.name);
    if (brand) {
      return brand;
    }
  }
  return null;
};

/**
 * Завод строки сравнения. Сначала по названию производства, потом по товарам
 * строки: у некоторых производств имя в прайсе и в номенклатуре записано
 * по-разному, а сойтись они должны.
 */
const brandsOfRow = (row) => {
  const brands = [extractProductionBrand(productionOf(row))];
  for (const material of (Array.isArray(row?.materialsToShow) ? row.materialsToShow : [])) {
    if (isGazName(material?.name)) {
      brands.push(extractGazBrand(material.name));
    }
  }
  return brands.filter(Boolean);
};

/**
 * @param {object}  params
 * @param {Array}   params.rows                — строки сравнения (sortedGroupedData)
 * @param {Array}   params.products            — товары, которые ввёл менеджер
 * @param {string}  params.selectedProduction  — выбранное производство (comparison.id)
 * @param {boolean} params.isMskGazobetonScope — признак области, приходит готовым
 * @returns {{brand: string, selectedProduction: string, best: object, difference: number}|null}
 *          null — искать нечего: бренд не определён, он же и выбран, или строк нет
 */
export const buildGazRequestedBrandHint = ({
  rows,
  products,
  selectedProduction,
  isMskGazobetonScope,
} = {}) => {
  if (!isMskGazobetonScope) {
    return null;
  }

  const brand = requestedBrandOf(products);
  if (!brand) {
    return null;
  }

  const usable = (Array.isArray(rows) ? rows : [])
    .filter((row) => !row?.isReagent && !row?.hasDeliveryError && totalOf(row) !== null && productionOf(row));

  const selectedKey = String(selectedProduction || '').trim();
  const selectedRow = usable.find((row) => comparisonIdOf(row) === selectedKey)
    || usable.find((row) => productionOf(row) === selectedKey)
    || null;

  if (!selectedRow) {
    return null;
  }

  const wanted = normalize(brand);
  // Выбран уже искомый завод — предлагать нечего.
  if (brandsOfRow(selectedRow).some((rowBrand) => normalize(rowBrand) === wanted)) {
    return null;
  }

  // У Bonolit и Исткульта несколько заводов, у ВКблока один: из подходящих
  // берётся самый выгодный по уже посчитанному итогу.
  const sameBrand = usable.filter((row) => brandsOfRow(row).some((rowBrand) => normalize(rowBrand) === wanted));
  if (sameBrand.length === 0) {
    return null;
  }

  const best = sameBrand.reduce((cheapest, row) => (totalOf(row) < totalOf(cheapest) ? row : cheapest), sameBrand[0]);

  return {
    brand,
    selectedProduction: productionOf(selectedRow),
    best: {
      production: productionOf(best),
      comparisonId: comparisonIdOf(best),
      total: totalOf(best),
    },
    // Больше нуля — искомый завод дороже выбранного. Разница считается из уже
    // готовых итогов сравнения.
    difference: Math.ceil(totalOf(best)) - Math.ceil(totalOf(selectedRow)),
  };
};

export default buildGazRequestedBrandHint;
